import React, { useMemo, useRef, useState } from 'react';
import { IDoorAssessment } from '../../../../entities-generated/common/door-assessment';
import * as formService from '../../form-services/assess-record-door';
import { EventNotificationService, useEventNotification } from '../../../../framework/services/event-notification';
import { ProjectEvents } from '../../events';
import { AssessmentDoorEditingModel, IAssessmentDoorEditingModel } from '../../entities/assess-rec-door-fm';
import { BwjSidePanel } from '../../../../framework/components/bwj-side-panel';
import { MessageBarType, PanelType } from '@fluentui/react';
import { BwjButton, BwjButtonType } from '../../../../framework/components/bwj-button';
import { BwjImageBrowser, IBrowserImage } from '../../../../framework/components/bwj-image-browser';
import { BwjGrid, Row, Col } from '../../../../framework/components/bwj-layout';
import { Spacing } from '../../../../framework/components/bwj-layout/styles';
import { BwjTextField } from '../../../../framework/components/bwj-text-field';
import { BwjUploadField } from '../../../../framework/components/bwj-upload';
import { BwjValidityState } from '../../../../framework/entities/bwj-validity-state';
import { BwjSingleSelectField } from '../../../../framework/components/bwj-select-field/single';
import { BwjCheckbox } from '../../../../framework/components/bwj-checkbox';
import { IStandardLabor } from '../../../../entities-generated/common/standard-labor';
import { ILaborCallout, LaborCallout } from '../../../../entities-generated/common/labor-callout';
import { ImageApiClient } from '../../../../services/image';
import { useToast } from '../../../../framework/components/bwj-toast/context';
import { any, firstOrDefault, remove, where } from '../../../../framework/utils/collection';
import { LaborListing } from '../labor-listing';
import { DoorType } from '../../../../entities-generated/common/door-type';
import { getNowUnixTimestamp } from '../../../../framework/utils/date-time';
import { AssessmentLaborEditingModel } from '../../entities/assess-rec-labor-fm';

interface IProposedDoorEditorPanelProps {
  onDoorUpdated: (door: IDoorAssessment) => void;
  onRemoveLabor: (labor: ILaborCallout, onConfirmed: (l: ILaborCallout) => void) => void;
}

const subscriberId = 'Panel.ProposedDoorEditor';

const getRelevantLabor = (type: DoorType, labor: IStandardLabor[]) => {
  const matches = (ty: DoorType, types: DoorType[]) => any(types, t => t === ty);
  return where(labor, l => matches(type, l.applicableTo));
}

export const ProposedDoorEditorPanel: React.FunctionComponent<IProposedDoorEditorPanelProps> = (props) => {
  const { showToast } = useToast();
  const [showPanel, setShowPanel] = useState(false);
  const blockPanelDismiss = useRef(false);
  const formModel = formService.useFormModel(subscriberId);
  const hasUnsavedChanges = formService.useHasUnsavedChanges(formModel, false);
  const currentArgs = useRef<IAssessmentDoorEditingModel>(new AssessmentDoorEditingModel());
  const [imageIndex, setImageIndex] = useState(0);
  const computedImageIndex = useMemo(() => {
    if(formModel.photoIds.length === 0) { return 0; }
    return imageIndex < formModel.photoIds.length ? imageIndex : formModel.photoIds.length - 1;
  }, [formModel, imageIndex]);
  const customLabor = useMemo(() => where(formModel.laborScope, l => !!l.standardLaborId === false), [formModel]);
  const standardLabor = useMemo(() => {
    const doorType = formModel.doorType.value?.id ?? DoorType.Indeterminate;
    return getRelevantLabor(doorType, currentArgs.current.standardLaborOptions);
  }, [formModel]);

  // we have to do this to defeat a bug where, when two panels are on display at the same time,
  // underlying panels are dismissed with any click on the screen.  The blockPanelDismiss flag
  // stops the panel from dismissing even when it wants to.  When this panel is free to be dismissable again
  // (because the top-most panel is gone), the event below is broadcast and consumed
  useEventNotification(() => {
    blockPanelDismiss.current = false;
  }, [], ProjectEvents.AssessmentEditorPanelFreeToClose, subscriberId);

  useEventNotification((args: IAssessmentDoorEditingModel) => {
    currentArgs.current = args;
    formService.initializeService(args);
    setShowPanel(true);
  }, [], ProjectEvents.AssessmentEditorPanel_Launch, subscriberId);

  const onRemoveImage = (image: IBrowserImage) => {
    ImageApiClient.removeImage(image.src)
    .then(() => {
      const photos = remove(formModel.photoIds, p => p === image);
      formService.photoIds.onUpdate(photos);
    })
    .catch(e => {
      console.log(e);
      showToast(MessageBarType.error, 'Image removal failed');
    });
  }

  const onEditLabor = (labor: ILaborCallout) => {
    blockPanelDismiss.current = true;
    const stdLabor = !!labor.standardLaborId
      ? firstOrDefault(currentArgs.current.standardLaborOptions, l => l.id === labor.standardLaborId) : null;
    const args = new AssessmentLaborEditingModel(
      labor, stdLabor, currentArgs.current.standardMaterialOptions
    );
    EventNotificationService.dispatch(ProjectEvents.LaborEditorPanel_Launch, args);
  };

  const onAddCustomLabor = () => {
    const labor = new LaborCallout();
    labor.id = getNowUnixTimestamp().toString();

    onEditLabor(labor);
  };

  const onAddStandardLabor = (std: IStandardLabor) => {
    const labor: ILaborCallout = {
      standardLaborId: std.id,
      id: getNowUnixTimestamp().toString(),
      description: std.description,
      customerSummary: std.customerSummary,
      time: std.time,
      materials: []
    };

    onEditLabor(labor);
  };

  const onRemoveLabor = (labor: ILaborCallout) => {
    const workItems = remove(formModel.laborScope, l => l.id === labor.id);
    formService.laborScope.onUpdate(workItems);
  }
  const onRequestRemoveLabor = (labor: ILaborCallout) => {
    blockPanelDismiss.current = true;
    props.onRemoveLabor(labor, onRemoveLabor);
  }

  const onClosePanel = () => blockPanelDismiss.current === false && setShowPanel(false);

  const onSaveChanges = () => {
    formService.validateAndUpdateModel()
    .then(result => {
      if(result.isValid) {
        const door = formService.toDoorAssessment();
        props.onDoorUpdated(door);
        setShowPanel(false);
      }
    });
  }

  return (
    <BwjSidePanel
      showPanel={showPanel}
      title={'Edit Proposed Door'}
      panelType={PanelType.large}
      footerButtons={[{
        buttonStyle: BwjButtonType.PrimaryStandard,
        text: 'Save changes',
        disabled: hasUnsavedChanges === false,
        onClick: onSaveChanges
      }, {
        buttonStyle: BwjButtonType.DefaultStandard,
        text: 'Cancel',
        onClick: onClosePanel
      }]}
      onDismiss={onClosePanel}
    >
      <BwjGrid>
        <Row bottomSpacing={Spacing.Large}>
          <Col small={12}>
            <BwjTextField
              id={formService.alias.id}
              required={true}
              label={formService.alias.description}
              onUpdate={formService.alias.onUpdate}
              input={formModel.alias}
            />
          </Col>
        </Row>
        <Row bottomSpacing={Spacing.Large}>
          <Col small={12}>
            <BwjSingleSelectField
              id={formService.doorType.id}
              required={true}
              label={formService.doorType.description}
              onUpdate={formService.doorType.onUpdate}
              input={formModel.doorType}
              options={formService.doorType.options}
              optionDisplayKey={'description'}
              optionValueKey={'id'}
            />
          </Col>
        </Row>
        {!!formModel.doorType.value &&
        <Row bottomSpacing={Spacing.Large}>
          <Col small={12} large={6}>
            <BwjSingleSelectField
              id={formService.size.id}
              required={true}
              label={formService.size.description}
              onUpdate={formService.size.onUpdate}
              input={formModel.size}
              options={formService.size.options}
              optionDisplayKey={'description'}
              optionValueKey={'id'}
            />
          </Col>
          <Col small={12} large={6}>
            <BwjSingleSelectField
              id={formService.handing.id}
              required={true}
              label={formService.handing.description}
              onUpdate={formService.handing.onUpdate}
              input={formModel.handing}
              options={formService.handing.options}
              optionDisplayKey={'description'}
              optionValueKey={'id'}
            />
          </Col>
        </Row>}
        <Row bottomSpacing={Spacing.Large}>
          <Col small={12} large={6}>
            <BwjCheckbox
              id={formService.cutdownRequired.id}
              label={formService.cutdownRequired.description}
              checked={formModel.cutdownRequired}
              onChange={formService.cutdownRequired.onUpdate}
            />
          </Col>
          <Col small={12} large={6}>
            <BwjCheckbox
              id={formService.existingDoorPresent.id}
              label={formService.existingDoorPresent.description}
              checked={formModel.existingDoorPresent}
              onChange={formService.existingDoorPresent.onUpdate}
            />
          </Col>
        </Row>
        <Row bottomSpacing={Spacing.Large}>
          <Col small={12} large={8}>
            <BwjUploadField
              id={formService.photoIdsUpload.id}
              required={false}
              label={formService.photoIdsUpload.description}
              onUpdate={formService.photoIdsUpload.onUpdate}
              input={formModel.photoIdsUpload}
              accept={['jpg', 'jpeg', 'png']}
              disabled={formModel.photoIdsUpload.validity === BwjValidityState.Indeterminate}
              allowMultipleFiles={true}
            />
          </Col>
          <Col small={12} large={4}>
            <BwjImageBrowser
              id={formService.photoIds.id}
              images={formModel.photoIds}
              displayIndex={computedImageIndex}
              loadImages={true}
              imageWidth={400}
              contextButton={{
                icon: 'Delete',
                onClick: onRemoveImage,
              }}
              onDisplayIndexChanged={idx => setImageIndex(idx)}
            />
          </Col>
        </Row>
        <Row bottomSpacing={Spacing.Large}>
          <Col small={12}>
            <h2>Additional Labor</h2>
          </Col>
        </Row>
        <Row bottomSpacing={Spacing.Large}>
          <Col small={12}>
            {standardLabor.map(stdLabor =>
              <LaborListing
                key={stdLabor.id}
                standardLabor={stdLabor}
                labor={firstOrDefault(formModel.laborScope, l => l.standardLaborId === stdLabor.id)}
                onAddLabor={onAddStandardLabor}
                onEditLabor={onEditLabor}
                onRemoveLabor={onRequestRemoveLabor}
              />
            )}
            {customLabor.map(labor =>
              <LaborListing
                key={labor.id}
                labor={labor}
                onAddLabor={onAddStandardLabor}
                onEditLabor={onEditLabor}
                onRemoveLabor={onRequestRemoveLabor}
              />
            )}
          </Col>
        </Row>
        <Row bottomSpacing={Spacing.Large}>
          <Col small={12}>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <BwjButton
                buttonStyle={BwjButtonType.PrimaryStandard}
                text={'Add custom labor'}
                onClick={onAddCustomLabor}
              />
            </div>
          </Col>
        </Row>
      </BwjGrid>
    </BwjSidePanel>
  );
};
